import React, { useState } from 'react';
import * as consts from 'config/consts';
import NumberLabel from 'components/features/utils/NumberLabel';
import Icon from 'components/commons/icon/Icon';

import {
  getPositions,
  GetPositionsResultItem,
  GetUserBrokerResultItem,
  UpdateAccountSymbolAvailable,
  UpdateAccountSymbolAvailableResult,
} from 'config/api';
import { CurrencyMode } from 'hooks/useCurrencyMode';
import { useModal } from 'hooks/useModal';
import { useRouting } from 'hooks/useRouting';
import { commaFormat } from '../new-report/utils/formatter';
import {
  AccountType,
  AccountTypeLabel,
  BrokerType,
} from '../utils/accountType';
import { DataCooperationModal } from '../modal/DataCooperationModal';
import { Side, sideToString } from '../utils/side';
import {
  isForeignStockTypes,
  isNotExistDetailTypes,
  ProductTypesLabel,
} from '../utils/productTypes';
import { isSymbolEmpty as isSymbolEmpty_ } from '../utils/util';
import { ChangeAggregatedModal } from '../modal/ChangeAggregatedModal';

// memo: ユーザが集計対象/対象外を決めることが出来る商品
const alignmentCashList = [
  {
    symbolName: 'SBIハイブリッド預金',
    isin: 'MONO-0018423',
  },
  {
    symbolName: 'スィープ専用銀行口座残高',
    isin: 'CASHJPY_SBI-SWP',
  },
];

type PositionitemProps = {
  data: GetPositionsResultItem;
  brokerItem: GetUserBrokerResultItem;
  currencyMode: CurrencyMode;
  setUserPositions?: (positions: Array<GetPositionsResultItem>) => void;
};

const PositionItem: React.FC<PositionitemProps> = props => {
  const { data, brokerItem, setUserPositions } = props;

  const [isAggregated, setIsAggregated] = useState<boolean>(data.Available);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const isForeignStock = isForeignStockTypes(data.ProductType);
  const isInvestmentTrust = data.ProductType === consts.productTypes.投資信託;
  const isNotExistDetail = isNotExistDetailTypes(data.ProductType);
  const isSymbolEmpty = isSymbolEmpty_(data.Symbol);

  // MEMO: 『類推中』はMONO-***、『類推完了』はDC-***で返却
  const isDataCooperation = isInvestmentTrust && /^MONO/.test(data.Symbol);
  const isAnalogyComplete =
    isInvestmentTrust && (data.Symbol === '' || /^DC/.test(data.Symbol));

  const brokerAlignmentCash = alignmentCashList.find(
    list => list.symbolName === data.SymbolName
  );
  const isBrokerAlignmentCash =
    brokerAlignmentCash === undefined ? false : true;

  const isStockMargin = data.CashMargin === consts.CashMargin.信用;

  // ロボアド判定用
  const compBrokerId = [
    consts.roboType.minkabu,
    consts.roboType.wealthnabi,
    consts.roboType.theo,
  ] as string[];

  const disableColor = 'text-bluegray-300';

  const amount =
    props.currencyMode === 'FOREIGN' && isForeignStock
      ? data.CurrentAmountUSD === null
        ? '---'
        : commaFormat(data.CurrentAmountUSD ?? 0, { numberOfDecimals: 2 })
      : data.CurrentAmount === null
      ? '---'
      : commaFormat(data.CurrentAmount ?? 0);
  const profitOrLoss =
    props.currencyMode === 'FOREIGN' && isForeignStock
      ? data.ProfitOrLossUSD
      : data.ProfitOrLoss;
  const unit =
    props.currencyMode === 'FOREIGN' && isForeignStock
      ? data.CurrencyUnit ?? ''
      : '円';
  const profitOrLossColor = isDataCooperation
    ? [disableColor, disableColor]
    : ['', ''];

  const [
    isShowDataCooperationModal,
    {
      hideModal: hideDataCooperationModal,
      showModal: showDataCooperationModal,
    },
  ] = useModal();

  const [
    isShowChangeAggregatedModal,
    {
      hideModal: hideChangeAggregatedModal,
      showModal: showChangeAggregatedModal,
    },
  ] = useModal();

  const routing = useRouting();
  const gotoDetail = (): void => {
    if (isNotExistDetail || isSymbolEmpty) return;
    if (isDataCooperation || isAnalogyComplete) return;

    if (
      !compBrokerId.includes(
        data.BrokerID as typeof consts.roboType[keyof typeof consts.roboType]
      )
    ) {
      routing.pushRouting(
        { path: `/tradedetail/${data.Symbol}` },
        true,
        window.scrollY
      );
    }
  };

  const isGoDetail =
    compBrokerId.includes(data.BrokerID) ||
    isNotExistDetail ||
    isSymbolEmpty ||
    isDataCooperation ||
    isAnalogyComplete
      ? false
      : true;

  const updateAccountSymbolAvailable = async (): Promise<UpdateAccountSymbolAvailableResult> => {
    setIsUpdating(true);
    const isin =
      brokerAlignmentCash !== undefined ? brokerAlignmentCash.isin : '';

    const res = await UpdateAccountSymbolAvailable({
      account: data.Account,
      isin,
      available: !isAggregated,
    });
    if (setUserPositions !== undefined) {
      getPositions().then(ret => {
        ret && setUserPositions(ret.Items);
      });
    }

    setIsUpdating(false);

    return res;
  };

  const cashItem = (
    <>
      <div className="flex py-3 ui-border-b w-full">
        <div className="flex flex-col flex-grow pl-2 text-bluegray-700">
          <div className="flex flex-row items-center">
            <AccountTypeLabel
              accountType={data.AccountType as AccountType}
              brokerType={brokerItem.BrokerType as BrokerType}
            />
            <div className="mr-1" />
            <ProductTypesLabel
              productType={data.ProductType}
              cashMargin={data.CashMargin}
            />
            {isBrokerAlignmentCash ? (
              <div
                onClick={() => showChangeAggregatedModal()}
                className={`flex ml-auto text-xs cursor-pointer ${
                  isAggregated ? 'text-green-500' : disableColor
                } tap-color-none`}>
                集計対象{!isAggregated ? '外' : ''}
                <div className="toggle ml-2">
                  <div className="checkbox">
                    <input
                      id={brokerItem.Account}
                      type="checkbox"
                      checked={isAggregated}
                      disabled={isUpdating}
                      onChange={(): void => {
                        return;
                      }}
                    />
                    <label htmlFor={brokerItem.Account}></label>
                  </div>
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
          <div
            className={`grid items-baseline mt-2 ${
              !isBrokerAlignmentCash
                ? ''
                : isBrokerAlignmentCash && data.Available && data.UpdatedFlg
                ? ''
                : disableColor
            }`}
            style={{ gridTemplateColumns: 'auto 1fr auto' }}>
            <div className="font-bold text-base truncate mr-2">
              {data.SymbolName}
            </div>
            <div
              className="ml-auto font-bold text-lg flex flex-row"
              style={{ whiteSpace: 'nowrap' }}>
              {amount}
              <div className="text-md">{unit}</div>
            </div>
          </div>
          {isBrokerAlignmentCash && data.UpdatedFlg === false ? (
            <div className="flex flex-row items-center mt-2 text-sm">
              <div className="icon-alert text-red-500"></div>
              <div className="ml-1">反映にお時間がかかる場合があります。</div>
            </div>
          ) : (
            <></>
          )}
        </div>
        {/* memo: コンテンツの幅を揃えるためにダミーの要素を入れておく */}
        <div className="ml-1 w-6"></div>
      </div>
      <ChangeAggregatedModal
        hide={hideChangeAggregatedModal}
        isShow={isShowChangeAggregatedModal}
        setIsAggregated={setIsAggregated}
        updateAccountSymbolAvailable={updateAccountSymbolAvailable}
      />
    </>
  );

  return (
    <>
      {isDataCooperation && (
        <div className="bg-red-30 rounded">
          <div className="flex flex-row items-center mt-2 p-2 text-sm">
            <div className="icon-alert text-red-500"></div>
            <div className="ml-1">データ取得中です</div>
            <div
              className="ml-auto mr-2 text-link cursor-pointer"
              onClick={showDataCooperationModal}>
              詳細を確認
            </div>
          </div>
        </div>
      )}
      {data.ProductType === consts.productTypes.預金_MRF ? (
        <>{cashItem}</>
      ) : (
        <div
          className={`flex py-3 ui-border-b w-full ${
            isGoDetail ? 'cursor-pointer' : ''
          }`}
          onClick={gotoDetail}>
          <div className="flex flex-col flex-grow pl-2 text-bluegray-700">
            <div className="flex flex-row items-center">
              <AccountTypeLabel
                accountType={data.AccountType as AccountType}
                brokerType={brokerItem.BrokerType as BrokerType}
              />
              <div className="mr-1" />
              <ProductTypesLabel
                productType={data.ProductType}
                cashMargin={data.CashMargin}
              />
              {data.CashMargin === consts.CashMargin.信用 ? (
                <>
                  <div className={`ml-1 text-1.5xs ${disableColor}`}>
                    {sideToString(data.Side as Side)}
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
            <div
              className={`grid items-baseline mt-2 ${
                isDataCooperation ? disableColor : ''
              }`}
              style={{ gridTemplateColumns: 'auto 1fr auto' }}>
              {!isSymbolEmpty ? (
                <div className="text-sm mr-2">{data.Symbol}</div>
              ) : null}
              <div className="font-bold text-base truncate mr-2">
                {data.SymbolName}
              </div>
              {!isStockMargin ? (
                <>
                  <div
                    className="ml-auto font-bold text-lg flex flex-row"
                    style={{ whiteSpace: 'nowrap' }}>
                    {amount}
                    <div className="text-md">{unit}</div>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
            <div className="flex mt-2">
              <div
                className={`flex ml-auto items-center ${
                  !compBrokerId.includes(data.BrokerID) ? 'ml-2' : ''
                }`}>
                <div
                  className={`mr-3 text-sm ${
                    isDataCooperation ? disableColor : 'text-bluegray-500'
                  }`}>
                  評価損益
                </div>
                <NumberLabel
                  className={`flex-grow flex justify-end ${
                    isDataCooperation ? disableColor : ''
                  }`}
                  value={profitOrLoss}
                  unitName={unit}
                  numberOfDecimal={
                    props.currencyMode === 'FOREIGN' && isForeignStock ? 2 : 0
                  }
                  classNames={profitOrLossColor}
                  isDispalyNoValueUnit
                />
              </div>
            </div>
          </div>
          <div className="flex items-center ml-1">
            <Icon
              name={`chevron-right`}
              className={`text-2xl ${
                isGoDetail ? 'text-gray-500' : 'text-transparent'
              }`}
            />
          </div>
        </div>
      )}
      <DataCooperationModal
        hideModal={hideDataCooperationModal}
        isShowModal={isShowDataCooperationModal}
      />
    </>
  );
};

export default PositionItem;
